/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useMemo } from 'react';
import { useSetting } from '@airasia-rn/account';
import {
  SNAP_SEARCH_VIEW_FEATURE_FLAGS,
  SNAP_SEARCH_VIEW_FLAGS_DEFAULT,
} from '../configs/featureFlagConstant';
import type { SnapSearchViewFlags } from '../types';

type UseSnapSearchViewFeatureFlagResult = SnapSearchViewFlags & {
  isSettingsLoading: boolean;
};

/**
 * Reads SNAP search-view feature flags from remote settings.
 *
 * Must run under `AppConfigProvider` (`useSetting` → `useAppConfig`).
 * Host root layouts must not call this above the provider — use
 * `SnapNavigationProvider` (or an equivalent child) inside AppConfigProvider.
 */
export const useSnapSearchViewFeatureFlag = () => {
  const { value, isSettingsLoading } = useSetting(
    SNAP_SEARCH_VIEW_FEATURE_FLAGS,
  );

  const searchViewFlags = useMemo<SnapSearchViewFlags>(() => {
    if (!value) {
      return SNAP_SEARCH_VIEW_FLAGS_DEFAULT;
    }

    try {
      const parsedFlags = JSON.parse(value);

      if (parsedFlags && typeof parsedFlags === 'object') {
        return {
          ...SNAP_SEARCH_VIEW_FLAGS_DEFAULT,
          ...(parsedFlags as Partial<SnapSearchViewFlags>),
        };
      }

      return SNAP_SEARCH_VIEW_FLAGS_DEFAULT;
    } catch {
      return SNAP_SEARCH_VIEW_FLAGS_DEFAULT;
    }
  }, [value]);

  return {
    ...searchViewFlags,
    isSettingsLoading,
  } as UseSnapSearchViewFeatureFlagResult;
};
